import React, { useState, useEffect } from 'react';
import {
  EditorState,
  convertToRaw,
  ContentState,
  convertFromRaw,
  convertFromHTML,
} from 'draft-js';
import { Editor } from 'react-draft-wysiwyg';
import draftToHtml from 'draftjs-to-html';
import { SdmTypography } from '../SdmTypography';

import FormLabel from '@material-ui/core/FormLabel';
import FormControl from '@material-ui/core/FormControl';

import { Controller } from 'react-hook-form';
import { styled } from 'components/styled';
import { theme } from 'theme';
import { makeStyles } from '@material-ui/core';
import { useIntl } from 'react-intl';
import translations from 'utils/translations';

const optionsWithImage = [
  'inline',
  'blockType',
  'fontSize',
  'fontFamily',
  'list',
  'textAlign',
  'colorPicker',
  'link',
  'image',
  'history',
];

const options = [
  'inline',
  'blockType',
  'fontSize',
  'fontFamily',
  'list',
  'textAlign',
  'colorPicker',
  'link',
  'history',
];

const useStyles = makeStyles({
  '@keyframes fadeIn': {
    '0%': {
      top: 0,
      opacity: 0,
    },
    '100%': {
      top: '30%',
      opacity: 1,
    },
  },
  '@keyframes fadeOut': {
    '0%': {
      top: '30%',
      opacity: 1,
    },
    '100%': {
      top: 0,
      opacity: 0,
    },
  },
  snackBar: {
    visibility: 'hidden',
    minWidth: 250,
    position: 'absolute',
    top: '30%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    padding: 16,
    textAlign: 'center',
    borderRadius: 5,
    backgroundColor: theme.colors.primary.red,
    color: '#fff',
    zIndex: 2,
    fontFamily:
      "'Suisse-Intl','Source_Sans_Pro-Regular','Roboto-Regular',Arial,Verdana,Sans Serif",
    fontSize: 16,
    fontWeight: 400,
  },
  show: {
    visibility: 'visible',
    animation: '$fadeIn 0.5s, $fadeOut 0.5s 2.5s',
  },
});

const CustomFormControl = styled(FormControl)({
  margin: theme.spacing(1),
  minWidth: 120,
  flex: 1,
  display: 'flex',
});

const CustomFormLabel = styled(FormLabel)({
  marginBottom: 5,
  paddingBottom: 0,
  color: 'black',
  fontWeight: 'bold',
  fontSize: 14,
});

const formControlLabelPlacementTop = {
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
};

const formLabelLabelPlacementTop = {
  marginBottom: 0,
  paddingRight: 8,
};

const variants = {
  grey: theme.colors.greyScale.backgroundGrey,
  white: theme.colors.greyScale.white,
  transparent: 'transparent',
};

export const SdmRichText = ({
  readOnly,
  value,
  onChange,
  onCountingChar = false,
  maxChar = false,
  isImageEnabled,
  label,
  displayLabel = true,
  labelPlacementLeft,
  formControlStyle,
  uploadFunction,
  variant = 'grey',
  editorStyleOverride = {},
  toolbarOverride = {},
  stripPastedStyles = true,
  defaultValue = '',
  editorClassName,
  labelStyle: labelStyleProp = {},
}) => {
  // Hooks
  const intl = useIntl();
  const i18n = translations.productPage(intl);
  const [snackBar, setSnackBar] = useState(false);
  const classes = useStyles();

  const controlStyle = {
    ...formControlStyle,
    ...(labelPlacementLeft && formControlLabelPlacementTop),
    ...(maxChar && {
      position: 'relative',
    }),
  };
  const labelStyle = {
    ...(labelPlacementLeft ? formLabelLabelPlacementTop : {}),
    ...labelStyleProp,
  };
  const [editorState, setEditorState] = useState(EditorState.createEmpty());

  useEffect(() => {
    if (value) {
      setEditorState(EditorState.createWithContent(convertFromRaw(value)));
    }
  }, [value]);

  useEffect(() => {
    if (defaultValue !== '') {
      const _state = EditorState.createWithContent(
        ContentState.createFromBlockArray(convertFromHTML(defaultValue))
      );
      setEditorState(_state);
    }
  }, [defaultValue]);

  const onEditorStateChange = async (newState) => {
    setEditorState(newState);
    const raw = convertToRaw(newState.getCurrentContent());
    const countingChar =
      maxChar && newState.getCurrentContent().getPlainText('').length;
    if (maxChar) onCountingChar?.(countingChar);
    onChange && onChange(raw);
  };

  //uploadFunction MUST return object like this { data: { link: www.google.com/image } }
  /* eslint-disable */
  const uploadImageCallBack = (file) => {
    return new Promise(async (resolve, reject) => {
      try {
        const uploadedFile = await uploadFunction(file);
        resolve(uploadedFile);
      } catch (err) {
        console.error(err);
        reject();
      }
    });
  };

  const _handleSnackbar = () => {
    setSnackBar(true);
    setTimeout(function () {
      setSnackBar(false);
    }, 3000);
  };

  const _handleBeforeInput = () => {
    const currentContent = editorState.getCurrentContent();
    const currentContentLength = currentContent.getPlainText('').length;
    if (currentContentLength > maxChar - 1) {
      _handleSnackbar();
      return 'handled';
    }
  };

  const _handlePastedText = (pastedText) => {
    const currentContent = editorState.getCurrentContent();
    const currentContentLength = currentContent.getPlainText('').length;
    if (currentContentLength + pastedText.length > maxChar) {
      _handleSnackbar();
      return 'handled';
    }
  };

  /* eslint-enable */

  return (
    <>
      <CustomFormControl style={controlStyle}>
        {displayLabel ? (
          <CustomFormLabel style={labelStyle}>{label}</CustomFormLabel>
        ) : (
          <></>
        )}
        {maxChar ? (
          <div className={`${classes.snackBar} ${snackBar && classes.show}`}>
            {i18n.tooltip.errorChar}
          </div>
        ) : null}
        <div
          style={{
            background: variants[variant],
            borderRadius: 4,
            display: 'flex',
            flex: 1,
          }}
        >
          <Editor
            editorClassName={editorClassName}
            stripPastedStyles={stripPastedStyles}
            readOnly={readOnly}
            toolbarHidden={readOnly}
            wrapperStyle={{
              flex: 1,
            }}
            editorStyle={{
              flex: 1,
              background: variants[variant],
              marginRight: 10,
              paddingLeft: 10,
              minHeight: 180,
              maxHeight: 180,
              overflowY: 'auto',
              ...editorStyleOverride,
            }}
            toolbarStyle={{
              background: variants[variant],
              flex: 1,
            }}
            editorState={editorState}
            handleBeforeInput={(value) => maxChar && _handleBeforeInput(value)}
            handlePastedText={(value) => maxChar && _handlePastedText(value)}
            toolbar={{
              options: isImageEnabled ? optionsWithImage : options,
              inline: { inDropdown: false },
              blockType: { inDropdown: true, className: 'toolbar-dropdown' },
              fontSize: { inDropdown: true, className: 'toolbar-dropdown' },
              fontFamily: { inDropdown: true, className: 'toolbar-dropdown' },
              list: { inDropdown: false },
              textAlign: { inDropdown: false },
              link: { inDropdown: false },
              remove: {
                icon: undefined,
                className: undefined,
                component: undefined,
              },

              history: { inDropdown: false },
              image: {
                icon: undefined,
                className: undefined,
                component: undefined,
                popupClassName: 'toolbar-dropdown',
                urlEnabled: true,
                uploadEnabled: true,
                alignmentEnabled: true,
                uploadCallback: uploadImageCallBack,
                previewImage: true,
                inputAccept:
                  'image/gif,image/jpeg,image/jpg,image/png,image/svg',
                alt: { present: false, mandatory: false },
                defaultSize: {
                  height: 'auto',
                  width: 'auto',
                },
              },
              ...toolbarOverride,
            }}
            onEditorStateChange={onEditorStateChange}
          />
        </div>
      </CustomFormControl>
    </>
  );
};

export const richTextToHTML = (raw) => {
  const markup = raw ? draftToHtml(raw) : '';
  return markup;
};

export const SdmRichTextForm = ({
  control,
  name,
  defaultValue,
  errors,
  ...rest
}) => (
  <>
    <Controller
      control={control}
      name={name}
      defaultValue={defaultValue}
      render={(props) => (
        <SdmRichText
          value={props.value}
          onChange={(e) => props.onChange(e)}
          {...rest}
        />
      )}
    />
    {errors && <SdmTypography variant={'red'}>{errors}</SdmTypography>}
  </>
);
